<template>
  <el-drawer
    title=""
    :visible.sync="isVisibleDrawerOrder"
    size="94%"
    :append-to-body="false"
    :modal-append-to-body="false"
    :show-close="false"
    :modal="false"
    v-loading.fullscreen.lock="isLoading"
    element-loading-text="載入中..."
  >
    <template #title>
      <div style="display: flex; align-items: center;">
        <el-button type="text" icon="el-icon-close" @click="onClickCloseDrawerOrder" style="font-size: 32px; font-weight:500" />
      </div>
    </template>

    <Step1hotelBooking v-if="currentStep === 1" @next-step="goToStep" @update-total="setTotal" />
    <Step2hotelBooking v-if="currentStep === 2" :currentStep="currentStep" @back-step="goToStep" />
    <Step3hotelBooking v-if="currentStep === 3" :currentStep="currentStep" @back-step="goToStep" @update-total="setTotal" />
    <BottomBar :currentStep="currentStep" :totalPrice="totalPrice" @next-step="goToStep" @build-booking-order="buildBookingOrder" @cancel-order="onClickCloseDrawerOrder" />
  </el-drawer>
</template>

<script>
import { mapGetters } from 'vuex'

import Step1hotelBooking from './Step1HotelBooking.vue'
import Step2hotelBooking from './Step2HotelBooking.vue'
import Step3hotelBooking from './Step3HotelBooking.vue'
import BottomBar from './partials/BottomBar.vue'
import API from '../../api'

export default {
  name: 'hotelBookingOrder',
  components: {
    Step1hotelBooking,
    Step2hotelBooking,
    Step3hotelBooking,
    BottomBar,
  },
  props: {
    // 控制建立訂單流程抽屜顯示與否
    value: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // 1: 選擇資訊, 2: 選擇寵物, 3: 付款確認
      currentStep: 1,
      totalPrice: 0,
      isLoading: false,
    }
  },
  computed: {
    isVisibleDrawerOrder: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    ...mapGetters('hotel/order', ['selectedBookingRoomsList', 'currentMemberInfo', 'priceAdjustments', 'addonItems', 'selectedPets', 'orderNote', 'adminBackfillMode']),
    ...mapGetters('hotel/setting', ['currentProduct']),
  },
  methods: {
    goToStep(step) {
      this.currentStep = step
    },
    setTotal(val) {
      this.totalPrice = val
    },
    async buildBookingOrder(close) {
      this.isLoading = true
      try {
        if (this.adminBackfillMode) {
          const roomTotal = this.selectedBookingRoomsList.reduce((sum, b) => sum + (Number(b.dateRoom?.priceStartAt) || 0), 0)
          const hasAdjustments = Array.isArray(this.priceAdjustments) && this.priceAdjustments.length > 0
          const hasAddonItems = Array.isArray(this.addonItems) && this.addonItems.length > 0
          if (roomTotal === 0 && !hasAdjustments && !hasAddonItems) {
            await this.$confirm('目前房費加總為 0 元且尚未設定自訂加減價，確定要建立訂單嗎？建議先於步驟中加價或於自訂加減價補足金額。', '補登訂單確認', {
              confirmButtonText: '仍要建立',
              cancelButtonText: '返回修改',
              type: 'warning',
            })
          }
        }
        await this.$confirm('若入住寵物為 「需評估」狀態，需人員確認接單<br/>再提供客戶付款資訊', '確認建立訂單', {
          confirmButtonText: '是',
          cancelButtonText: '否',
          dangerouslyUseHTMLString: true,
        })

        const payload = this.composeOrderPayload()

        const res = await API.order.postHotelOrder(this.currentProduct.id, payload)
        this.$message.success('已建立訂單')
        this.handleSuccessReset(close)

        this.$emit('reload-order-list')
        //  視覺緩衝 1 秒
        await new Promise((resolve) => setTimeout(resolve, 1000))
        try {
          await this.$confirm('是否主動前往訂單詳情頁面?<br/>(付款等後續步驟)', '前往訂單詳請', {
            confirmButtonText: '是',
            cancelButtonText: '否',
            dangerouslyUseHTMLString: true,
          })

          const query = this.$route.name === 'schedule-check-in' ? { from: 'schedule-check-in' } : {}
          this.$router.push({
            name: 'hotelViewOrder',
            params: { productId: this.currentProduct.id, containerId: res.orders[0].containerId }, // 修正 key 值
            query,
          })
        } catch (err) {
          if (err !== 'cancel') console.error('取消主動前往付款', err)
        }
      } catch (err) {
        if (err === 'cancel') {
          this.$message.warning('已取消建立訂單')
        } else {
          this.$message.error('提交訂單失敗，請稍後再試')
          console.error('Order Error:', err)
        }
      } finally {
        this.isLoading = false
      }
    },
    composeOrderPayload() {
      const normalizedPriceAdjustments = (this.priceAdjustments || []).map((item) => {
        const amount = Math.abs(Number(item.amount) || 0)
        const qty = Math.max(1, Number(item.qty || item.quantity || 1))
        return {
          type: item.type,
          title: item.reason || item.title || '-',
          amount,
          quantity: qty,
        }
      })

      const normalizedAddonItems = (this.addonItems || []).map((item) => ({
        hotelGoodsId: item.addonId,
        petId: item.petId,
        qty: Math.max(1, Number(item.qty || item.quantity || 1)),
      }))

      const base = {
        memberId: this.currentMemberInfo?.id,
        nights: this.selectedBookingRoomsList.length,
        note: this.orderNote,
        guests: this.selectedPets.map((p) => ({ id: p.id, name: p.name })),
        bookings: this.selectedBookingRoomsList.map((b) => ({
          roomTypeId: b.type?.id,
          title: b.room?.roomTitle,
          roomId: b.roomId,
          roomNumber: Number(b.room?.roomTitle?.match(/\d+/g)?.pop()) || null,
          date: b.date,
          price: Number(b.dateRoom?.priceStartAt) || 0,
          note: null,
        })),
        custom_adjustments: normalizedPriceAdjustments.map((adj) => ({
          type: adj.type,
          title: adj.title,
          quantity: adj.quantity,
          amount: ['minus', 'discount'].includes(adj.type) ? -Math.abs(adj.amount) : Math.abs(adj.amount),
        })),
        addons: normalizedAddonItems,
      }
      if (this.adminBackfillMode) {
        base.adminBackfill = true
      }
      return base
    },
    handleSuccessReset(close) {
      this.isVisibleDrawerOrder = close
      this.currentStep = 1
      this.$store.commit('hotel/order/RESET_STATE')
      this.$emit('clear-chosen-rooms')
    },
    async onClickCloseDrawerOrder() {
      try {
        await this.$confirm('一旦離開步驟流程將重新選取資料', '是否離開建立住宿訂單流程', {
          confirmButtonText: '是',
          cancelButtonText: '否',
        })

        this.$message.success('已離開建立住宿訂單流程')

        this.isVisibleDrawerOrder = false
        this.currentStep = 1

        this.$store.commit('hotel/order/RESET_STATE')
        this.$emit('clear-chosen-rooms')
      } catch (err) {
        console.error(err)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .el-drawer__header {
  position: fixed;
  top: 0px;
  left: 75px;
  padding: 0px;
}

::v-deep .el-drawer__close-btn {
  font-size: 2.5rem;
}

::v-deep .el-drawer__body {
  padding: 0px;
  padding-bottom: 50px;
}
</style>
